import { useAlerts } from "components/Alerts/AlertProvider";
import pluralize from "pluralize";
import { useCallback } from "react";

/**
 * The warning for co-located copies a write left unchanged, or null when it
 * reached every site. Only setting an assignee can be refused for the
 * assignee's access, so `blameAssignee` is false for everything else.
 */
export function skippedCopiesWarning(
  skippedCopyCount: number,
  blameAssignee: boolean
): string | null {
  if (skippedCopyCount === 0) {
    return null;
  }
  const reasons = blameAssignee
    ? "the site is archived, you can't edit it, or the assignee can't access it"
    : "the site is archived, or you can't edit it";
  return `Not changed at ${pluralize("other site", skippedCopyCount, true)}: ${reasons}.`;
}

/**
 * Announces a saved write in ONE alert, since the alert bar shows only the
 * latest: the success message, or a warning carrying it when copies were skipped.
 */
export function useAnnounceColocatedWrite(): (
  successMessage: string,
  result: { skippedCopyCount: number; blameAssignee: boolean }
) => void {
  const alerts = useAlerts();
  return useCallback(
    (successMessage, { skippedCopyCount, blameAssignee }) => {
      const warning = skippedCopiesWarning(skippedCopyCount, blameAssignee);
      if (warning) {
        alerts.warning(`${successMessage}. ${warning}`);
      } else {
        alerts.success(successMessage);
      }
    },
    [alerts]
  );
}
